<template>
  <Dialog
    :visible="visible"
    modal
    :header="$t('ProjectManagement.closure.title')"
    :style="{ width: '640px', maxWidth: '95vw' }"
    :closable="!submitting"
    data-testid="pm-closure-wizard"
    @update:visible="onVisibleUpdate"
  >
    <div class="mb-5">
      <p class="text-[13px] text-[var(--color-disabled)] m-0 mb-4">
        {{ $t('ProjectManagement.closure.stepOf', { step: step + 1, label: stepLabels[step] }) }}
      </p>
      <div class="flex items-center gap-2">
        <div
          v-for="(label, index) in stepLabels"
          :key="label"
          class="flex items-center gap-2 flex-1 min-w-0"
        >
          <div
            class="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0"
            :style="stepBadgeStyle(index)"
          >
            <i v-if="index < step" class="pi pi-check text-xs" />
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span
            class="text-[12px] font-medium truncate"
            :style="{ color: index === step ? 'var(--color-dark)' : 'var(--color-disabled)' }"
          >
            {{ label }}
          </span>
          <div
            v-if="index < stepLabels.length - 1"
            class="flex-1 h-px mx-1"
            :style="{ backgroundColor: index < step ? '#16A34A' : '#E5E5E5' }"
          />
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-10 text-center text-[var(--color-disabled)]" data-testid="pm-closure-loading">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('ProjectManagement.closure.loading') }}
    </div>

    <Message v-else-if="error" severity="error" :closable="false" class="mb-3">
      {{ error }}
    </Message>

    <template v-else-if="summary">
      <!-- Step 1: Assessment -->
      <div v-if="step === 0" class="space-y-4" data-testid="pm-closure-step-assessment">
        <div>
          <h3 class="text-[16px] font-semibold text-[var(--color-dark)] m-0">
            {{ $t('ProjectManagement.closure.assessmentTitle') }}
          </h3>
          <p class="text-[13px] text-[var(--color-disabled)] mt-0.5 mb-0">
            {{ $t('ProjectManagement.closure.assessmentHint') }}
          </p>
        </div>

        <div class="p-4 rounded-[12px] bg-[#F9F9F9]" data-testid="pm-closure-ppc-card">
          <p class="text-[13px] text-[var(--color-disabled)] m-0">
            {{ $t('ProjectManagement.closure.ppcLabel') }}
          </p>
          <p class="text-[32px] font-bold mt-1 mb-0" style="color: #e4821a">
            {{ formatPercent(summary.ppcPercent) }}
          </p>
          <p class="text-[13px] text-[var(--color-disabled)] mt-1 mb-0">
            {{ $t('ProjectManagement.closure.ppcTarget', {
              target: summary.ppcTargetPercent,
              gap: formatGap(summary.ppcGap),
            }) }}
          </p>
        </div>

        <div>
          <label class="text-[13px] font-medium text-[var(--color-dark)] mb-1.5 block">
            {{ $t('ProjectManagement.closure.wentWell') }}
          </label>
          <Textarea
            v-model="wentWell"
            rows="3"
            class="w-full"
            :placeholder="$t('ProjectManagement.closure.wentWellPlaceholder')"
            data-testid="pm-closure-went-well"
          />
        </div>

        <div>
          <label class="text-[13px] font-medium text-[var(--color-dark)] mb-1.5 block">
            {{ $t('ProjectManagement.closure.improveNext') }}
          </label>
          <Textarea
            v-model="improveNext"
            rows="3"
            class="w-full"
            :placeholder="$t('ProjectManagement.closure.improveNextPlaceholder')"
            data-testid="pm-closure-improve-next"
          />
        </div>
      </div>

      <!-- Step 2: Summary -->
      <div v-else-if="step === 1" class="space-y-4" data-testid="pm-closure-step-summary">
        <div>
          <h3 class="text-[16px] font-semibold text-[var(--color-dark)] m-0">
            {{ $t('ProjectManagement.closure.summaryTitle') }}
          </h3>
          <p class="text-[13px] text-[var(--color-disabled)] mt-0.5 mb-0">
            {{ $t('ProjectManagement.closure.summaryHint') }}
          </p>
        </div>

        <div class="grid grid-cols-3 gap-3">
          <div
            v-for="tile in summaryTiles"
            :key="tile.id"
            class="p-4 text-center rounded-[12px] border border-[#E5E5E5]"
            :data-testid="`pm-closure-tile-${tile.id}`"
          >
            <p class="text-[24px] font-bold text-[var(--color-dark)] m-0">{{ tile.value }}</p>
            <p class="text-[12px] text-[var(--color-disabled)] mt-1 mb-0">{{ tile.label }}</p>
            <p class="text-[11px] text-[var(--color-disabled)] mt-0.5 mb-0">{{ tile.sub }}</p>
          </div>
        </div>

        <div
          v-if="summary.hasOpenItems"
          class="flex items-start gap-3 p-3.5 rounded-[10px] bg-[#FEF3C7]"
          data-testid="pm-closure-open-warning"
        >
          <i class="pi pi-exclamation-triangle text-[var(--color-warning-text)] mt-0.5" />
          <p class="text-[13px] text-[var(--color-dark)] m-0">
            {{ $t('ProjectManagement.closure.openWarning', {
              constraints: summary.openWarnings.openConstraints,
              commitments: summary.openWarnings.openCommitments,
              decisions: summary.openWarnings.openDecisions,
              milestones: summary.openWarnings.openMilestones,
            }) }}
          </p>
        </div>

        <Button
          :label="$t('ProjectManagement.closure.downloadPdf')"
          icon="pi pi-download"
          severity="secondary"
          :loading="downloading"
          data-testid="pm-closure-download-pdf"
          @click="downloadPdf"
        />
      </div>

      <!-- Step 3: Confirm -->
      <div v-else class="space-y-4" data-testid="pm-closure-step-confirm">
        <div>
          <h3 class="text-[16px] font-semibold text-[var(--color-dark)] m-0">
            {{ $t('ProjectManagement.closure.confirmTitle') }}
          </h3>
          <p class="text-[13px] text-[var(--color-disabled)] mt-0.5 mb-0">
            {{ $t('ProjectManagement.closure.confirmHint') }}
          </p>
        </div>

        <div class="flex items-start gap-3 p-5 rounded-[12px] bg-[#FEF2F2]">
          <i class="pi pi-exclamation-triangle text-[var(--color-error-text)] text-xl mt-0.5" />
          <div>
            <p class="text-[14px] font-bold text-[var(--color-dark)] m-0">
              {{ $t('ProjectManagement.closure.irreversibleTitle') }}
            </p>
            <p class="text-[13px] text-[var(--color-disabled)] mt-1 mb-0">
              {{ $t('ProjectManagement.closure.irreversibleBody') }}
            </p>
          </div>
        </div>

        <label class="flex items-start gap-3 cursor-pointer">
          <Checkbox
            v-model="confirmed"
            binary
            input-id="pm-closure-confirm"
            data-testid="pm-closure-confirm"
          />
          <span class="text-[14px] text-[var(--color-dark)]">
            {{ $t('ProjectManagement.closure.confirmCheckbox') }}
          </span>
        </label>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between w-full gap-3">
        <Button
          v-if="step === 0"
          :label="$t('ProjectManagement.cancel')"
          severity="secondary"
          text
          :disabled="submitting"
          data-testid="pm-closure-cancel"
          @click="close"
        />
        <Button
          v-else
          :label="$t('ProjectManagement.closure.back')"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          :disabled="submitting"
          data-testid="pm-closure-back"
          @click="step -= 1"
        />

        <div class="flex gap-2 ml-auto">
          <Button
            v-if="step < 2"
            :label="$t('ProjectManagement.closure.next')"
            icon="pi pi-arrow-right"
            icon-pos="right"
            class="btn-primary-accent"
            unstyled
            :disabled="loading || !summary"
            data-testid="pm-closure-next"
            @click="step += 1"
          />
          <Button
            v-else
            :label="$t('ProjectManagement.closure.closeProject')"
            class="btn-primary-accent"
            unstyled
            :disabled="!confirmed || submitting"
            :loading="submitting"
            data-testid="pm-closure-submit"
            @click="submitClose"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import pmProjectClosureService from '@/services/pmProjectClosureService';
import pmProjectService from '@/services/pmProjectService';

export default {
  name: 'ProjectClosureWizard',
  components: {
    Dialog,
    Button,
    Textarea,
    Checkbox,
    Message,
  },
  props: {
    visible: { type: Boolean, default: false },
    project: { type: Object, default: null },
  },
  emits: ['update:visible', 'closed'],
  data() {
    return {
      step: 0,
      loading: false,
      downloading: false,
      submitting: false,
      error: null,
      summary: null,
      wentWell: '',
      improveNext: '',
      confirmed: false,
    };
  },
  computed: {
    stepLabels() {
      return [
        this.$t('ProjectManagement.closure.steps.assessment'),
        this.$t('ProjectManagement.closure.steps.summary'),
        this.$t('ProjectManagement.closure.steps.close'),
      ];
    },
    summaryTiles() {
      if (!this.summary) return [];
      return [
        {
          id: 'milestones',
          value: this.summary.milestones.total,
          label: this.$t('ProjectManagement.closure.tiles.milestones'),
          sub: this.$t('ProjectManagement.closure.tiles.milestonesSub', {
            done: this.summary.milestones.done,
          }),
        },
        {
          id: 'commitments',
          value: this.summary.commitments.total,
          label: this.$t('ProjectManagement.closure.tiles.commitments'),
          sub: this.$t('ProjectManagement.closure.tiles.commitmentsSub', {
            ppc: this.formatPercent(this.summary.ppcPercent),
          }),
        },
        {
          id: 'constraints',
          value: this.summary.constraints.total,
          label: this.$t('ProjectManagement.closure.tiles.constraints'),
          sub: this.$t('ProjectManagement.closure.tiles.constraintsSub', {
            resolved: this.summary.constraints.resolved ?? this.summary.constraints.done,
            open: this.summary.constraints.open,
          }),
        },
      ];
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(open) {
        if (open) this.resetAndLoad();
      },
    },
  },
  methods: {
    stepBadgeStyle(index) {
      if (index < this.step) return { backgroundColor: '#16A34A', color: '#FFFFFF' };
      if (index === this.step) return { background: 'var(--accent-gradient, #e4821a)', color: '#FFFFFF' };
      return { backgroundColor: '#F2F2F2', color: '#ABABAB' };
    },
    formatPercent(value) {
      return typeof value === 'number' ? `${value}%` : '—';
    },
    formatGap(value) {
      if (typeof value !== 'number') return '—';
      return value > 0 ? `+${value}%` : `${value}%`;
    },
    onVisibleUpdate(value) {
      this.$emit('update:visible', value);
    },
    close() {
      this.$emit('update:visible', false);
    },
    async resetAndLoad() {
      this.step = 0;
      this.error = null;
      this.summary = null;
      this.wentWell = '';
      this.improveNext = '';
      this.confirmed = false;
      if (!this.project?.id) return;

      this.loading = true;
      try {
        this.summary = await pmProjectClosureService.getClosureSummary(this.project.id);
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || this.$t('ProjectManagement.closure.error');
      } finally {
        this.loading = false;
      }
    },
    async downloadPdf() {
      if (!this.project?.id) return;
      this.downloading = true;
      try {
        const response = await pmProjectClosureService.downloadClosureReport(this.project.id, {
          wentWell: this.wentWell,
          improveNext: this.improveNext,
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `project-closure-${this.project.name || this.project.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || this.$t('ProjectManagement.closure.pdfError');
      } finally {
        this.downloading = false;
      }
    },
    async submitClose() {
      if (!this.confirmed || !this.project?.id) return;
      this.submitting = true;
      this.error = null;
      try {
        await pmProjectService.updateProject(this.project.id, { status: 'completed' });
        this.$emit('closed', this.project.id);
        this.close();
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || this.$t('ProjectManagement.closure.error');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
